import i18n from 'i18next'

import { AlertCircleIcon, AlertTriangleIcon, CircleCheckIcon, InfoIcon, XIcon } from 'lucide-react'

import { toast as sonnerToast } from 'sonner'

const iconProps = { size: 18, 'aria-hidden': true }

function renderIcon(type: 'success' | 'error' | 'warning' | 'info') {
  switch (type) {
    case 'success':
      return <CircleCheckIcon className='text-green-600' {...iconProps} />
    case 'error':
      return <AlertCircleIcon className='text-red-600' {...iconProps} />
    case 'warning':
      return <AlertTriangleIcon className='text-yellow-600' {...iconProps} />
    case 'info':
      return <InfoIcon className='text-blue-600' {...iconProps} />
  }
}

type ToastType = 'success' | 'error' | 'warning' | 'info'
type ToastPosition = 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right'

interface ToastOptions {
  position?: ToastPosition
  backgroundColor?: string
  textColor?: string
  borderColor?: string
  offset?: number
  rtl?: boolean
  duration?: number // Duration in milliseconds
}

const rtlPositionMap: Record<ToastPosition, ToastPosition> = {
  'top-left': 'top-right',
  'top-center': 'top-center',
  'top-right': 'top-left',
  'bottom-left': 'bottom-right',
  'bottom-center': 'bottom-center',
  'bottom-right': 'bottom-left',
}

function toast(message: string, type: ToastType = 'info', options: ToastOptions = {}) {
  const rtl = options.rtl ?? i18n.dir() === 'rtl'

  let { position = 'top-right', backgroundColor, borderColor, textColor, offset = 2.5, duration = 3500 } = options

  if (rtl) {
    position = rtlPositionMap[position]
  }

  const isTop = position.startsWith('top')
  const isBottom = position.startsWith('bottom')

  const computedBackground = backgroundColor ?? (type === 'error' ? '#FEE2E2' : 'white')
  const computedBorderColor = borderColor ?? (type === 'error' ? '#FEE2E2' : '#ededed')
  const computedTextColor = textColor ?? (type === 'error' ? '#991B1B' : '#1F2937')

  sonnerToast.custom(
    (t) => (
      <div
        dir={rtl ? 'rtl' : undefined}
        className={`z-50 flex w-full max-w-sm items-start gap-4 rounded-lg border border-gray-200 bg-white p-3 shadow-lg transition-all duration-300 dark:border-neutral-700 dark:bg-neutral-900 ${
          rtl ? 'flex-row-reverse' : 'flex-row'
        }`}
        role='alert'
        style={{
          backgroundColor: computedBackground,
          borderColor: computedBorderColor,
          color: computedTextColor,
          ...(isTop && { marginTop: `${offset}rem` }),
          ...(isBottom && { marginBottom: `${offset}rem` }),
        }}
      >
        <div className='mt-0.5 shrink-0'>{renderIcon(type)}</div>
        <div className='flex-1 text-sm leading-relaxed'>{message}</div>
        <button
          onClick={() => sonnerToast.dismiss(t)}
          className={`group shrink-0 rounded p-1 transition hover:bg-black/5 dark:hover:bg-white/5 ${
            rtl ? 'mr-2 ml-0' : 'mr-0 ml-2'
          }`}
          aria-label='Close notification'
        >
          <XIcon size={16} className='opacity-60 group-hover:opacity-100' aria-hidden='true' />
        </button>
      </div>
    ),
    { position, duration }
  )
}

export default {
  success: (msg: string, options?: ToastOptions) => toast(msg, 'success', options),
  error: (msg: string, options?: ToastOptions) => toast(msg, 'error', options),
  warning: (msg: string, options?: ToastOptions) => toast(msg, 'warning', options),
  info: (msg: string, options?: ToastOptions) => toast(msg, 'info', options),
}
